import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messagingApi } from '@shared/api/messaging';
import { useDoctorControlStore } from '@shared/stores/doctor-control-store';
import type {
  CreateDirectRoomRequest,
  OpenQuoteRoomRequest,
  StartMessagingCallRequest,
} from '@jema-manager/shared/types/messaging.types';

export const messagingQueryKeys = {
  all: ['messaging'] as const,
  rooms: (doctorControlRequestId?: string | null) =>
    doctorControlRequestId
      ? ([...messagingQueryKeys.all, 'rooms', 'doctor-control', doctorControlRequestId] as const)
      : ([...messagingQueryKeys.all, 'rooms', 'default'] as const),
  room: (roomId: string, doctorControlRequestId?: string | null) =>
    doctorControlRequestId
      ? ([
          ...messagingQueryKeys.all,
          'room',
          'doctor-control',
          doctorControlRequestId,
          roomId,
        ] as const)
      : ([...messagingQueryKeys.all, 'room', 'default', roomId] as const),
  operationsOverview: (doctorControlRequestId?: string | null) =>
    doctorControlRequestId
      ? ([
          ...messagingQueryKeys.all,
          'operations-overview',
          'doctor-control',
          doctorControlRequestId,
        ] as const)
      : ([...messagingQueryKeys.all, 'operations-overview', 'default'] as const),
  routingTriage: (doctorControlRequestId?: string | null) =>
    doctorControlRequestId
      ? ([
          ...messagingQueryKeys.all,
          'routing-triage',
          'doctor-control',
          doctorControlRequestId,
        ] as const)
      : ([...messagingQueryKeys.all, 'routing-triage', 'default'] as const),
};

const MESSAGING_ROOM_REFETCH_INTERVAL_MS = 15_000;

const useDoctorControlRequestId = () =>
  useDoctorControlStore(state => state.activeRequest?.id ?? null);

const emptyRoomQueryKey = (doctorControlRequestId?: string | null) =>
  doctorControlRequestId
    ? ([
        ...messagingQueryKeys.all,
        'room',
        'doctor-control',
        doctorControlRequestId,
        'none',
      ] as const)
    : ([...messagingQueryKeys.all, 'room', 'default', 'none'] as const);

export const useMessagingRooms = (
  options: { enabled?: boolean; refetchInterval?: number | false } = {}
) => {
  const doctorControlRequestId = useDoctorControlRequestId();

  return useQuery({
    queryKey: messagingQueryKeys.rooms(doctorControlRequestId),
    queryFn: () => messagingApi.listRooms(),
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval ?? MESSAGING_ROOM_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
};

export const useMessagingRoom = (roomId: string | null) => {
  const doctorControlRequestId = useDoctorControlRequestId();

  return useQuery({
    queryKey: roomId
      ? messagingQueryKeys.room(roomId, doctorControlRequestId)
      : emptyRoomQueryKey(doctorControlRequestId),
    queryFn: () => messagingApi.getRoom(roomId ?? ''),
    enabled: Boolean(roomId),
  });
};

export const useMessagingOperationalOverview = (options: { enabled?: boolean } = {}) => {
  const doctorControlRequestId = useDoctorControlRequestId();

  return useQuery({
    queryKey: messagingQueryKeys.operationsOverview(doctorControlRequestId),
    queryFn: () => messagingApi.getOperationalOverview(),
    enabled: options.enabled ?? true,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
};

export const useMessagingRoutingTriage = (options: { enabled?: boolean } = {}) => {
  const doctorControlRequestId = useDoctorControlRequestId();

  return useQuery({
    queryKey: messagingQueryKeys.routingTriage(doctorControlRequestId),
    queryFn: () => messagingApi.listRoutingTriage(),
    enabled: options.enabled ?? true,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
};

export const useResolveMessagingRoutingTriage = () => {
  const queryClient = useQueryClient();
  const doctorControlRequestId = useDoctorControlRequestId();

  return useMutation({
    mutationFn: (assignments: Array<{ decisionId: string; roomId: string }>) =>
      messagingApi.resolveRoutingTriage({ assignments }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: messagingQueryKeys.routingTriage(doctorControlRequestId),
      });
      void queryClient.invalidateQueries({
        queryKey: messagingQueryKeys.rooms(doctorControlRequestId),
      });
    },
  });
};

export const useClarifyMessagingRoutingTriage = () => {
  const queryClient = useQueryClient();
  const doctorControlRequestId = useDoctorControlRequestId();

  return useMutation({
    mutationFn: (input: { decisionId: string; prompt: string }) =>
      messagingApi.clarifyRoutingTriage(input.decisionId, {
        prompt: input.prompt,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: messagingQueryKeys.routingTriage(doctorControlRequestId),
      });
    },
  });
};

export const useOpenQuoteRoom = () => {
  const queryClient = useQueryClient();
  const doctorControlRequestId = useDoctorControlRequestId();

  return useMutation({
    mutationFn: (input: OpenQuoteRoomRequest) => messagingApi.openQuoteRoom(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: messagingQueryKeys.rooms(doctorControlRequestId),
      });
    },
  });
};

export const useCreateDirectRoom = () => {
  const queryClient = useQueryClient();
  const doctorControlRequestId = useDoctorControlRequestId();

  return useMutation({
    mutationFn: (input: CreateDirectRoomRequest) => messagingApi.createDirectRoom(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: messagingQueryKeys.rooms(doctorControlRequestId),
      });
    },
  });
};

export const useStartMessagingCall = (roomId: string | null) => {
  const queryClient = useQueryClient();
  const doctorControlRequestId = useDoctorControlRequestId();

  return useMutation({
    mutationFn: (input: StartMessagingCallRequest) => {
      if (!roomId) throw new Error('roomId is required');
      return messagingApi.startCall(roomId, input);
    },
    onSuccess: () => {
      if (!roomId) return;
      void queryClient.invalidateQueries({
        queryKey: messagingQueryKeys.room(roomId, doctorControlRequestId),
      });
    },
  });
};
